import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import Switch from 'antd/lib/switch';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { globalMessage } from '@/utils/globalMessage';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import Checkbox from 'antd/lib/checkbox';
import {
    selectRequiresFixRequestsApprovals,
    selectVCSSupportsStatusChecks,
    selectEnableStatusChecksForFixRequests,
    selectAddFixRequestsToOpenPRsAgainstReleaseBranches,
    selectThreadFixNotifications,
    selectNotificationIntegration,
    selectFixRequestsAutoMergeUserCreatedPRsSettings,
    selectRequiredFixRequestsApprovals,
    selectPreventFixRequestsAfterSubmissionCustomCopy,
    selectPreventFixRequestsAfterSubmissionCustomCopyEnabled,
    selectPreventFixRequestsAfterSubmission,
    selectFixRequestFormFieldsSettings,
} from '@/selectors/selectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Divider from 'antd/lib/divider';
import { RootState } from 'reducers/reducers';
import { MergeType, IntegrationType, FixRequestFormFieldSettings } from '@/types';
import { Button, InputNumber, Select, Space, Tooltip } from 'antd/lib';
import { Option } from 'antd/lib/mentions';
import { useWatch } from 'antd/es/form/Form';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { InfoCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';

interface EditFixesSettingsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditFixesSettingsModal: React.FC<EditFixesSettingsModalProps> = (props: EditFixesSettingsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const addFixRequestsToOpenPRsAgainstReleaseBranches = useSelector(
        selectAddFixRequestsToOpenPRsAgainstReleaseBranches(props.appId),
    );
    const requiresFixRequestsApprovals = useSelector(selectRequiresFixRequestsApprovals(props.appId));
    const requiredFixRequestsApprovals = useSelector(selectRequiredFixRequestsApprovals(props.appId));
    const vcsSupportsStatusChecks = useSelector(selectVCSSupportsStatusChecks(props.appId, props.orgId));
    const enableStatusChecksForFixRequests = useSelector(selectEnableStatusChecksForFixRequests(props.appId));
    const threadFixNotifications = useSelector(selectThreadFixNotifications(props.appId));
    const fixRequestFormFieldsSettings = useSelector(selectFixRequestFormFieldsSettings(props.appId));
    const preventFixRequestsAfterSubmissionCustomCopy = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopy(props.appId),
    );
    const preventFixRequestsAfterSubmissionCustomCopyEnabled = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopyEnabled(props.appId),
    );
    const preventFixRequestsAfterSubmission = useSelector(selectPreventFixRequestsAfterSubmission(props.appId));
    const { mergeUserCreatedPRs, workingBranchUserCreatedPRMergeType, releaseBranchUserCreatedPRMergeType } =
        useSelector(selectFixRequestsAutoMergeUserCreatedPRsSettings(props.appId));

    const editFixesSettings = (value) => {
        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            addFixRequestsToOpenPRsAgainstReleaseBranches: value.addFixRequestsToOpenPRsAgainstReleaseBranches,
            requiresFixRequestsApprovals: value.requireApprovals,
            requiredFixRequestsApprovals: value.requiredApprovals || 1,
            enableStatusChecksForFixRequests: value.requireApprovals && value.enableStatusChecks,
            // No fieldId is stored on settings — it is derived from the field name where needed.
            fixRequestFormFieldsSettings: value.customFields ?? [],
            threadFixNotifications: value.threadFixNotifications,
            mergeUserCreatedPRs: value.mergeUserCreatedPRs,
            workingBranchUserCreatedPRMergeType: value.workingBranchUserCreatedPRMergeType ?? [],
            releaseBranchUserCreatedPRMergeType: value.releaseBranchUserCreatedPRMergeType,
            preventFixRequestsAfterSubmission: value.preventFixRequestsAfterSubmission,
            preventFixRequestsAfterSubmissionCustomCopyEnabled:
                value.preventFixRequestsAfterSubmissionCustomCopyEnabled,
            preventFixRequestsAfterSubmissionCustomCopy: value.preventFixRequestsAfterSubmissionCustomCopy,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    const updateFixesFeatureSettingsIsLoading = useIsLoading(AppSettingsUpdatable.RequiresFixRequestsApprovals);
    const updateFixesFeatureSettingsSuccess = useUpdated(AppSettingsUpdatable.RequiresFixRequestsApprovals);
    const updateFixesFeatureSettingsError = useError(AppSettingsUpdatable.RequiresFixRequestsApprovals);

    const notificationIntegration = useSelector((state: RootState) =>
        selectNotificationIntegration(state, props.appId),
    );
    const isMergeUserCreatedPRsEnabled = useWatch('mergeUserCreatedPRs', form);
    const isRequireApprovalsEnabled = useWatch('requireApprovals', form);
    const watchedFormFields = useWatch('customFields', form);
    const isPreventFixRequestsAfterSubmissionEnabled = useWatch(['preventFixRequestsAfterSubmission'], form);
    const isPreventFixRequestsAfterSubmissionCustomCopyEnabled = useWatch(
        ['preventFixRequestsAfterSubmissionCustomCopyEnabled'],
        form,
    );

    // threading is only supported in Slack
    const hasSlackIntegration =
        notificationIntegration != null ? notificationIntegration.id === IntegrationType.Slack : false;

    React.useEffect(() => {
        if (updateFixesFeatureSettingsSuccess) {
            globalMessage.success('Fixes settings updated successfully!');
            props.onCancel();
        }
    }, [updateFixesFeatureSettingsSuccess]);

    React.useEffect(() => {
        if (updateFixesFeatureSettingsError) {
            globalMessage.error(updateFixesFeatureSettingsError);
        }
    }, [updateFixesFeatureSettingsError]);

    const initialValues = {
        addFixRequestsToOpenPRsAgainstReleaseBranches: addFixRequestsToOpenPRsAgainstReleaseBranches,
        requireApprovals: requiresFixRequestsApprovals,
        requiredApprovals: requiredFixRequestsApprovals,
        enableStatusChecks: enableStatusChecksForFixRequests,
        threadFixNotifications: threadFixNotifications,
        customFields: fixRequestFormFieldsSettings,
        mergeUserCreatedPRs: mergeUserCreatedPRs,
        workingBranchUserCreatedPRMergeType: workingBranchUserCreatedPRMergeType,
        releaseBranchUserCreatedPRMergeType: releaseBranchUserCreatedPRMergeType,
        preventFixRequestsAfterSubmission: preventFixRequestsAfterSubmission,
        preventFixRequestsAfterSubmissionCustomCopy: preventFixRequestsAfterSubmissionCustomCopy,
        preventFixRequestsAfterSubmissionCustomCopyEnabled: preventFixRequestsAfterSubmissionCustomCopyEnabled,
    };

    const displayMergeStrategyValue = (strategy: MergeType) => {
        switch (strategy) {
            case MergeType.DoNotMerge:
                return 'Do not merge user-created PRs';
            case MergeType.MergeCommit:
                return 'Merge user-created PRs with merge commit';
            case MergeType.Squash:
                return 'Squash merge user-created PRs';
        }
    };

    const mergeStrategyLabel = (targetBranch: string) => (
        <Text
            style={{
                color: isMergeUserCreatedPRsEnabled ? runwayColors.black : antGrey.grey5,
            }}
        >
            {targetBranch} branch
        </Text>
    );

    const mergeStrategyOptions = Object.values(MergeType).map((strategy) => {
        return (
            <Option value={strategy} key={strategy}>
                {displayMergeStrategyValue(strategy)}
            </Option>
        );
    });

    const tooltipForFormField = (helperText: string) => {
        return {
            title: <Text style={{ color: runwayColors.white }}>{helperText}</Text>,
            icon: (
                <Space style={{ marginLeft: 8, cursor: 'default' }}>
                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                </Space>
            ),
        };
    };

    return (
        <Modal
            title="Edit fixes settings"
            onOk={form.submit}
            okButtonProps={{ loading: updateFixesFeatureSettingsIsLoading }}
            okText="Save"
            open={props.open}
            onCancel={props.onCancel}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>
                    These settings will be used by Runway in regards to late-arriving work items that may still be
                    pulled into a release.
                </Text>
                <Form
                    onFinish={editFixesSettings}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="addFixRequestsToOpenPRsAgainstReleaseBranches"
                        valuePropName="checked"
                        label={'Create a fix for all open PRs against the release branch'}
                        tooltip={tooltipForFormField(
                            'A fix will be automatically created for any PRs opened against the release branch post-kickoff.',
                        )}
                    >
                        <Switch />
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="requireApprovals"
                        valuePropName="checked"
                        label={'Fix approvals'}
                        tooltip={tooltipForFormField(
                            'If enabled, fix requests can be approved or rejected by team members with sufficient privileges. Approvals can be performed on the Feature readiness step.',
                        )}
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item name="requiredApprovals" label={'Number of approvals required'}>
                        <InputNumber min={1} disabled={!isRequireApprovalsEnabled} />
                    </Form.Item>
                    <Form.Item
                        style={{ marginTop: 40 }}
                        name="enableStatusChecks"
                        hidden={!vcsSupportsStatusChecks}
                        valuePropName="checked"
                        tooltip={tooltipForFormField('')}
                    >
                        <Checkbox>Add pull request status check for fix request approvals</Checkbox>
                        <Tooltip title="Runway will include a status check on any pull requests created for fixes going into the release branch. The status check will only pass when the fix request associated with the PR has been approved.">
                            <InfoCircleOutlined
                                style={{
                                    color: antGrey.grey7,
                                }}
                            />
                        </Tooltip>
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        label={
                            <Space>
                                <span>Fix request form fields</span>
                                <Tooltip title="Define text fields that users must fill out when submitting a fix request.">
                                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                </Tooltip>
                            </Space>
                        }
                    >
                        <Form.List
                            name="customFields"
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        // Zero form fields is allowed. When fields exist, their names must be unique
                                        // (names become the field ids, so duplicates would collide).
                                        const fields = (value ?? []) as FixRequestFormFieldSettings[];
                                        const seen = new Set<string>();
                                        for (const field of fields) {
                                            const name = field?.name?.trim().toLowerCase();
                                            if (!name) continue;
                                            if (seen.has(name)) {
                                                return Promise.reject(new Error('Form field names must be unique'));
                                            }
                                            seen.add(name);
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            {(fields, { add, remove }, { errors }) => (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                                    {fields.map(({ key, name: fieldIndex, ...restField }) => (
                                        <div
                                            key={key}
                                            style={{
                                                border: `1px solid ${antGrey.grey4}`,
                                                borderRadius: 8,
                                                padding: 12,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                backgroundColor: runwayColors.white,
                                                position: 'relative',
                                            }}
                                        >
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                                onClick={() => remove(fieldIndex)}
                                                style={{ position: 'absolute', top: 6, right: 6 }}
                                            />
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'name']}
                                                label="Field name"
                                                style={{ marginBottom: 0, paddingRight: 32 }}
                                                rules={[{ required: true, message: 'Field name is required' }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'required']}
                                                valuePropName="checked"
                                                getValueFromEvent={(e) => e.target.checked}
                                                style={{ marginBottom: 0, marginTop: 10 }}
                                            >
                                                <Checkbox>Input required</Checkbox>
                                            </Form.Item>
                                            {watchedFormFields?.[fieldIndex]?.required && (
                                                <Form.Item
                                                    {...restField}
                                                    name={[fieldIndex, 'minCharacters']}
                                                    label="Minimum characters"
                                                    style={{ marginBottom: 0, marginTop: 6, marginLeft: 24 }}
                                                >
                                                    <InputNumber min={1} placeholder="None" />
                                                </Form.Item>
                                            )}
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'placeholder']}
                                                label="Placeholder text"
                                                style={{ marginBottom: 0, marginTop: 10 }}
                                            >
                                                <Input.TextArea rows={2} placeholder="Optional placeholder text" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'prePopulatedText']}
                                                label="Pre-populated text"
                                                style={{ marginBottom: 0, marginTop: 10 }}
                                            >
                                                <Input.TextArea
                                                    rows={3}
                                                    placeholder="Optional pre-populated text (e.g. for a template)"
                                                />
                                            </Form.Item>
                                        </div>
                                    ))}
                                    <Button
                                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                        onClick={() =>
                                            add({
                                                name: '',
                                                placeholder: '',
                                                prePopulatedText: '',
                                                required: false,
                                            } satisfies FixRequestFormFieldSettings)
                                        }
                                    >
                                        Add field
                                    </Button>
                                    <Form.ErrorList errors={errors} />
                                </div>
                            )}
                        </Form.List>
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="mergeUserCreatedPRs"
                        valuePropName="checked"
                        label={'Auto-merge user-created PRs with fixes'}
                        tooltip={tooltipForFormField(
                            'If enabled, user-created PRs with fixes against the selected target branches will be automatically merged if they are approved and have no conflicts.',
                        )}
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item name="workingBranchUserCreatedPRMergeType" label={mergeStrategyLabel('Working')}>
                        <Select disabled={!isMergeUserCreatedPRsEnabled} style={{ width: '100%' }}>
                            {mergeStrategyOptions}
                        </Select>
                    </Form.Item>
                    <Form.Item name="releaseBranchUserCreatedPRMergeType" label={mergeStrategyLabel('Release')}>
                        <Select disabled={!isMergeUserCreatedPRsEnabled} style={{ width: '100%' }}>
                            {mergeStrategyOptions}
                        </Select>
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="preventFixRequestsAfterSubmission"
                        valuePropName="checked"
                        style={{ marginBottom: 4 }}
                    >
                        <Checkbox>Disallow new fix requests once release is submitted</Checkbox>
                    </Form.Item>
                    {isPreventFixRequestsAfterSubmissionEnabled ?
                        <Form.Item
                            name="preventFixRequestsAfterSubmissionCustomCopyEnabled"
                            valuePropName="checked"
                            style={{ marginBottom: 4, marginLeft: 12 }}
                        >
                            <Checkbox>Show a message alongside disallowed action</Checkbox>
                        </Form.Item>
                    :   null}
                    {(
                        isPreventFixRequestsAfterSubmissionCustomCopyEnabled &&
                        isPreventFixRequestsAfterSubmissionEnabled
                    ) ?
                        <Form.Item
                            name="preventFixRequestsAfterSubmissionCustomCopy"
                            style={{ marginLeft: 12 }}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (
                                            isPreventFixRequestsAfterSubmissionCustomCopyEnabled &&
                                            isPreventFixRequestsAfterSubmissionEnabled &&
                                            !value
                                        ) {
                                            return Promise.reject(new Error('Custom copy is required'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input.TextArea rows={5} />
                        </Form.Item>
                    :   null}
                    <Divider />
                    {hasSlackIntegration && (
                        <Form.Item
                            name="threadFixNotifications"
                            valuePropName="checked"
                            label={'Thread related fix notifications'}
                            tooltip={tooltipForFormField(
                                'Notifications relevant to a given fix will be threaded in Slack',
                            )}
                        >
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default EditFixesSettingsModal;
